// searchUsers.js
// User search helpers for mentions

import {getAllUsers, getUserDoc, searchUsersByName} from "../../../../firebase/firestore/users";

function normalizeMentionUser(user) {
    if (!user || user.id === 'ai') return null;

    const label = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.id;

    // Resolve avatar: prefer explicit profilePicture (already an absolute or /api/ path from
    // normalizeApiUser), then headshot/photo, then build /api/users/{id}/avatar when hasAvatar.
    const uid = user.id || user.userId;
    let avatarUrl = user.profilePicture || user.headshot || user.photo || null;
    if (!avatarUrl && user.hasAvatar === true && uid) {
        avatarUrl = `/api/users/${encodeURIComponent(uid)}/avatar`;
    }

    return {
        id: uid,
        label,
        name: label,
        email: user.email || '',
        position: user.position || user.role || '',
        headshot: avatarUrl || '',
        hasAvatar: user.hasAvatar === true,
        isAvailable: user.isAvailable !== false,
    };
}

/**
 * Search users for mention suggestions
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @param {number} options.limit - Maximum results (default: 20)
 * @returns {Promise<Array>} Array of user objects with normalized fields
 */
export async function searchUsersForMention(query, options = {}) {
    const limit = options.limit || 20;

    try {
        let users = [];

        if (!query || !String(query).trim()) {
            // Show a small list of users when query is empty
            users = await getAllUsers(limit, {includeMachineUsers: true});
        } else {
            // Search users by name
            users = await searchUsersByName(query, {limit});
        }

        // Normalize user data for mention display
        return users
            .map(normalizeMentionUser)
            .filter(Boolean);
    } catch (err) {
        console.error('[searchUsersForMention] Error:', err);
        return [];
    }
}

export async function getUserForMention(userId) {
    if (!userId || userId === 'ai') return null;

    try {
        return normalizeMentionUser(await getUserDoc(userId));
    } catch (err) {
        console.error('[getUserForMention] Error:', err);
        return null;
    }
}

/**
 * Format user data for display in suggestion list
 * @param {Object} user - User object
 * @returns {Object} Formatted display data
 */
export function formatUserForDisplay(user) {
    return {
        title: user.label || user.name,
        subtitle: user.email || user.position || '',
        avatar: user.headshot || null,
    };
}

